/* ============================================================
   PH PERFUMERÍA
   PH Search Overlay v2.0

   Responsabilidades:
   - Abrir y cerrar el overlay.
   - Cerrar con ESC.
   - Cerrar mediante backdrop o botón.
   - Bloquear el scroll del body.
   - Administrar y devolver el foco.
   - Atrapar el foco dentro del diálogo.
   - Mantener compatibilidad con data-ph-search-open.
   ============================================================ */

(() => {
  'use strict';

  const SELECTORS = {
    overlay: '[data-ph-search-overlay]',
    panel: '[data-ph-search-panel]',
    openButton: '[data-ph-search-open]',
    closeButton: '[data-ph-search-close]'
  };

  const CLASSES = {
    open: 'is-open',
    bodyLock: 'ph-search-overlay-lock'
  };

  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  class PHSearchOverlay {
    constructor(overlay) {
      this.overlay = overlay;
      this.panel = overlay.querySelector(SELECTORS.panel);

      this.closeButtons = Array.from(
        overlay.querySelectorAll(SELECTORS.closeButton)
      );

      this.isOpen = false;
      this.lastFocusedElement = null;
      this.closeTimer = null;
      this.focusTimer = null;

      this.handleDocumentClick =
        this.handleDocumentClick.bind(this);

      this.handleKeydown =
        this.handleKeydown.bind(this);

      this.handleThemeEditorLoad =
        this.handleThemeEditorLoad.bind(this);

      this.init();
    }

    init() {
      if (!this.overlay || !this.panel) {
        return;
      }

      this.bindEvents();
    }

    bindEvents() {
      document.addEventListener(
        'click',
        this.handleDocumentClick
      );

      document.addEventListener(
        'keydown',
        this.handleKeydown
      );

      this.closeButtons.forEach((button) => {
        button.addEventListener('click', () => {
          this.close();
        });
      });

      document.addEventListener(
        'shopify:section:load',
        this.handleThemeEditorLoad
      );
    }

    handleDocumentClick(event) {
      const openTrigger = event.target.closest(
        SELECTORS.openButton
      );

      if (!openTrigger) {
        return;
      }

      event.preventDefault();
      this.open(openTrigger);
    }

    handleKeydown(event) {
      if (!this.isOpen) {
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key === 'Tab') {
        this.trapFocus(event);
      }
    }

    handleThemeEditorLoad(event) {
      if (!event?.target) {
        return;
      }

      initializeSearchOverlays(event.target);
    }

    open(trigger = null) {
      if (this.isOpen) {
        this.focusPrimaryElement();
        return;
      }

      window.clearTimeout(this.closeTimer);
      window.clearTimeout(this.focusTimer);

      this.lastFocusedElement =
        trigger instanceof HTMLElement
          ? trigger
          : document.activeElement;

      this.overlay.hidden = false;
      this.overlay.setAttribute('aria-hidden', 'false');

      document.body.classList.add(CLASSES.bodyLock);

      document
        .querySelectorAll(SELECTORS.openButton)
        .forEach((button) => {
          button.setAttribute('aria-expanded', 'true');
        });

      window.requestAnimationFrame(() => {
        this.overlay.classList.add(CLASSES.open);
        this.isOpen = true;

        this.focusTimer = window.setTimeout(() => {
          this.focusPrimaryElement();
        }, 90);
      });

      this.dispatchCustomEvent(
        'ph:search-overlay:open'
      );
    }

    close() {
      if (!this.isOpen) {
        return;
      }

      window.clearTimeout(this.focusTimer);

      this.overlay.classList.remove(CLASSES.open);
      this.overlay.setAttribute('aria-hidden', 'true');

      document.body.classList.remove(CLASSES.bodyLock);

      document
        .querySelectorAll(SELECTORS.openButton)
        .forEach((button) => {
          button.setAttribute('aria-expanded', 'false');
        });

      this.isOpen = false;

      this.closeTimer = window.setTimeout(() => {
        if (!this.isOpen) {
          this.overlay.hidden = true;
        }
      }, 230);

      this.restoreFocus();

      this.dispatchCustomEvent(
        'ph:search-overlay:close'
      );
    }

    focusPrimaryElement() {
      const searchInput = this.panel.querySelector(
        [
          '[data-ph-input]',
          'input[type="search"]',
          'input:not([type="hidden"])'
        ].join(',')
      );

      if (searchInput) {
        searchInput.focus();
        return;
      }

      const firstFocusable =
        this.getFocusableElements()[0];

      if (firstFocusable) {
        firstFocusable.focus();
        return;
      }

      this.panel.focus();
    }

    restoreFocus() {
      const element = this.lastFocusedElement;

      if (
        !element ||
        typeof element.focus !== 'function'
      ) {
        return;
      }

      window.setTimeout(() => {
        if (document.contains(element)) {
          element.focus();
        }
      }, 240);
    }

    getFocusableElements() {
      return Array.from(
        this.panel.querySelectorAll(
          FOCUSABLE_SELECTOR
        )
      ).filter((element) => {
        return (
          !element.hasAttribute('hidden') &&
          element.getAttribute('aria-hidden') !== 'true' &&
          element.offsetParent !== null
        );
      });
    }

    trapFocus(event) {
      const focusableElements =
        this.getFocusableElements();

      if (focusableElements.length === 0) {
        event.preventDefault();
        this.panel.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement =
        focusableElements[
          focusableElements.length - 1
        ];

      if (
        event.shiftKey &&
        document.activeElement === firstElement
      ) {
        event.preventDefault();
        lastElement.focus();
        return;
      }

      if (
        !event.shiftKey &&
        document.activeElement === lastElement
      ) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    dispatchCustomEvent(name, detail = {}) {
      document.dispatchEvent(
        new CustomEvent(name, {
          detail: {
            overlay: this.overlay,
            panel: this.panel,
            ...detail
          }
        })
      );
    }
  }

  function initializeSearchOverlays(root = document) {
    const overlays = [];

    if (
      root instanceof HTMLElement &&
      root.matches(SELECTORS.overlay)
    ) {
      overlays.push(root);
    }

    overlays.push(
      ...root.querySelectorAll(SELECTORS.overlay)
    );

    overlays.forEach((overlay) => {
      if (
        overlay.dataset.phOverlayInitialized ===
        'true'
      ) {
        return;
      }

      overlay.dataset.phOverlayInitialized = 'true';

      const instance = new PHSearchOverlay(
        overlay
      );

      overlay.phSearchOverlay = instance;
    });
  }

  function getMainOverlay() {
    return document.querySelector(
      SELECTORS.overlay
    );
  }

  window.PHOverlay = {
    open() {
      const overlay = getMainOverlay();

      if (!overlay?.phSearchOverlay) {
        return;
      }

      overlay.phSearchOverlay.open();
    },

    close() {
      const overlay = getMainOverlay();

      if (!overlay?.phSearchOverlay) {
        return;
      }

      overlay.phSearchOverlay.close();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initializeSearchOverlays();
      }
    );
  } else {
    initializeSearchOverlays();
  }
})();